"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGetCurrentRoleForUserQuery = exports.useGetRoleForUserQuery = exports.getRoleV2 = exports.useUpdateUserRoleQuery = exports.useRemoveRoleFromUserQuery = exports.useAddRoleToUserQuery = exports.useGetAdminRolesV2Query = exports.useGetTeamRolesV2Query = exports.useGetRolesV2Query = void 0;
const react_query_1 = require("@tanstack/react-query");
const lodash_1 = require("lodash");
const roles_1 = __importDefault(require("../../common/constants/roles"));
const appConstants_1 = require("../constants/appConstants");
const roleRepository_1 = __importDefault(require("../repositories/roleRepository"));
const Root_1 = require("../router/Root");
const userSelectors_1 = require("../selectors/userSelectors");
const zustandStore_1 = require("../zustandStore");
const companyQuery_1 = require("./companyQuery");
const query_keys_1 = require("./query-keys");
const teamQuery_1 = require("./teamQuery");
const userQuery_1 = require("./userQuery");
const { companyAdmin: COMPANY_ADMIN, tradeshiftSupportAdmin: TRADESHIFT_SUPPORT_ADMIN } = roles_1.default;
const useGetRolesV2Query = (role, tsAdmin) => {
    const { setRoles } = (0, zustandStore_1.useZustand)();
    return (0, react_query_1.useQuery)({
        queryKey: [query_keys_1.rolesKeys.setRoles, roles_1.default],
        queryFn: async () => {
            const user = await Root_1.queryClient.ensureQueryData(userQuery_1.userQuery);
            const companyPropertiesData = await Root_1.queryClient.ensureQueryData((0, companyQuery_1.companyPropertiesQuery)(user?.companyAccountId || '', { cardprovider: 'tsgo-cardprovider' }));
            setRoles({ roles: roles_1.default, cardprovider: companyPropertiesData?.cardprovider });
            return roles_1.default;
        },
        enabled: !role && !!tsAdmin,
    });
};
exports.useGetRolesV2Query = useGetRolesV2Query;
const useGetTeamRolesV2Query = (role, tsAdmin) => {
    const { setRoles } = (0, zustandStore_1.useZustand)();
    return (0, react_query_1.useQuery)({
        queryKey: [query_keys_1.rolesKeys.setRoles, roles_1.default],
        queryFn: async () => {
            const rolesData = (0, lodash_1.omit)(roles_1.default, [
                'companyAdmin',
                'tradeshiftSupportAdmin',
                'getRoleById',
                'getRoleOptionsObject',
                'admin',
                'basic',
                'member',
                'finance',
                'support',
            ]);
            const user = await Root_1.queryClient.ensureQueryData(userQuery_1.userQuery);
            const companyPropertiesData = await Root_1.queryClient.ensureQueryData((0, companyQuery_1.companyPropertiesQuery)(user?.companyAccountId || '', { cardprovider: 'tsgo-cardprovider' }));
            setRoles({ roles: rolesData, cardprovider: companyPropertiesData?.cardprovider });
            return rolesData;
        },
        enabled: !role && !tsAdmin,
    });
};
exports.useGetTeamRolesV2Query = useGetTeamRolesV2Query;
const useGetAdminRolesV2Query = role => {
    const { setRoles } = (0, zustandStore_1.useZustand)();
    return (0, react_query_1.useQuery)({
        queryKey: [query_keys_1.rolesKeys.setRoles, role?.id],
        queryFn: async () => {
            if (role?.id) {
                const rolesData = roles_1.default.getRoleOptionsObject(role?.id);
                const user = await Root_1.queryClient.ensureQueryData(userQuery_1.userQuery);
                const companyPropertiesData = await Root_1.queryClient.ensureQueryData((0, companyQuery_1.companyPropertiesQuery)(user?.companyAccountId || '', { cardprovider: 'tsgo-cardprovider' }));
                setRoles({
                    roles: rolesData,
                    cardprovider: companyPropertiesData?.cardprovider,
                });
                return rolesData;
            }
            else {
                return {};
            }
        },
        enabled: Boolean(role?.id),
    });
};
exports.useGetAdminRolesV2Query = useGetAdminRolesV2Query;
const useAddRoleToUserQuery = () => {
    return (0, react_query_1.useMutation)({
        mutationFn: roleRepository_1.default.addRoleToUser,
        onSuccess: () => {
            void Root_1.queryClient.invalidateQueries({ queryKey: [query_keys_1.userKeys.getUsersByRoleId(appConstants_1.overviewConstants.COMPANY_ADMIN)] });
            void Root_1.queryClient.invalidateQueries({ queryKey: [query_keys_1.userKeys.getUsersByRoleId(appConstants_1.overviewConstants.TEAM_ADMIN)] });
        },
    });
};
exports.useAddRoleToUserQuery = useAddRoleToUserQuery;
const useRemoveRoleFromUserQuery = (roleId) => {
    const queryClient = (0, react_query_1.useQueryClient)();
    return (0, react_query_1.useMutation)({
        mutationFn: (input) => roleRepository_1.default.removeRoleFromUser(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: [query_keys_1.userKeys.getUsersByRoleId(roleId)],
            });
        },
    });
};
exports.useRemoveRoleFromUserQuery = useRemoveRoleFromUserQuery;
const useUpdateUserRoleQuery = () => {
    const queryClient = (0, react_query_1.useQueryClient)();
    return (0, react_query_1.useMutation)({
        mutationFn: ({ userId, selectedRoleId, teamId }) => roleRepository_1.default.updateUserRole(userId, selectedRoleId, teamId),
        onSuccess: async () => {
            void queryClient.invalidateQueries({
                queryKey: [query_keys_1.teamKeys.getDecoratedUser],
            });
            void queryClient.invalidateQueries({
                queryKey: [query_keys_1.teamKeys.getTeamUsers],
            });
        },
    });
};
exports.useUpdateUserRoleQuery = useUpdateUserRoleQuery;
const getRoleV2 = key => {
    return roles_1.default[key];
};
exports.getRoleV2 = getRoleV2;
const getRoleById = (id) => (0, lodash_1.find)(roles_1.default, { id });
const useGetRoleForUserQuery = (user, teamId) => {
    const enabled = Boolean(user) && user?.roles && user?.roles.length > 0 && Boolean(teamId);
    return (0, react_query_1.useQuery)({
        queryKey: [query_keys_1.rolesKeys.setRole, user?.userId, teamId],
        queryFn: () => {
            const currentRole = user?.roles.find(role => role.securityGroupId === teamId);
            return getRoleById(currentRole?.roleId || '') || { id: '' };
        },
        enabled: Boolean(enabled),
    });
};
exports.useGetRoleForUserQuery = useGetRoleForUserQuery;
const useGetCurrentRoleForUserQuery = (user, teams, teamId) => {
    const { data: currentTeam } = (0, teamQuery_1.useGetCurrentTeamQuery)(teams, teamId);
    if (!user) {
        return { label: '' };
    }
    if ((0, userSelectors_1.isCompanyAdmin)(user?.roles)) {
        return COMPANY_ADMIN;
    }
    else if ((0, userSelectors_1.isSupportAdmin)(user?.roles)) {
        return TRADESHIFT_SUPPORT_ADMIN;
    }
    else {
        const effectiveTeamId = currentTeam?.id || teamId;
        const currentRole = user?.roles.find(role => role.securityGroupId === effectiveTeamId);
        return currentRole?.roleId ? getRoleById(currentRole.roleId) : { label: '' };
    }
};
exports.useGetCurrentRoleForUserQuery = useGetCurrentRoleForUserQuery;
